/**
 * Premium auto-sliding announcement bar.
 * Vanilla JS only — no dependencies.
 *
 * Progressive enhancement contract:
 * - The Liquid template renders slide 1 visible and all other slides with
 *   aria-hidden="true" at page-render time (see sections/announcement-bar.liquid).
 * - If this script never runs (JS disabled/blocked/errors), the first
 *   announcement still displays correctly with zero layout shift.
 * - This script only enhances that static markup with autoplay, swipe,
 *   keyboard control, hover-pause, and Theme Editor block selection.
 */
class AnnouncementSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayId = null;
    this.isHovered = false;
    this.isFocused = false;
    this.editing = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.announcement-slider__slide'));

    // Fewer than 2 slides: nothing to enhance, static markup is already correct.
    if (this.slides.length < 2) return;

    this.prevButton = this.querySelector('.announcement-slider__control--prev');
    this.nextButton = this.querySelector('.announcement-slider__control--next');

    this.speed = parseInt(this.dataset.speed, 10) || 3500;
    this.autoplayEnabled = this.dataset.autoplay !== 'false';

    // Sync internal index with whichever slide is currently visible in the DOM.
    const activeIndex = this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true');
    this.currentIndex = activeIndex > -1 ? activeIndex : 0;

    this.bindEvents();

    // Lazy initialization: defer autoplay start until the browser is idle
    // so the slider never competes with initial render/Lighthouse metrics.
    const start = () => this.startAutoplay();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 2000 });
    } else {
      window.setTimeout(start, 200);
    }

    document.addEventListener('visibilitychange', this.boundVisibilityChange);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.goTo(this.currentIndex - 1);
        this.restartAutoplay();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.goTo(this.currentIndex + 1);
        this.restartAutoplay();
      });
    }

    // Pause on hover (desktop). Touch devices don't fire mouseenter on tap,
    // so this naturally only affects pointer/desktop users.
    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.stopAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
      this.restartAutoplay();
    });

    // Keyboard friendly: arrow keys move slides when the component has focus.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
        this.restartAutoplay();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
        this.restartAutoplay();
      }
    });

    // Proper focus handling: pause while any part of the slider has focus.
    this.addEventListener('focusin', () => {
      this.isFocused = true;
      this.stopAutoplay();
    });
    this.addEventListener('focusout', () => {
      this.isFocused = false;
      this.restartAutoplay();
    });

    // Swipe support (mobile).
    this.addEventListener(
      'touchstart',
      (event) => {
        this.touchStartX = event.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (event) => {
        this.touchEndX = event.changedTouches[0].screenX;
        this.handleSwipe();
      },
      { passive: true }
    );

    // Theme Editor compatibility: when a merchant clicks an announcement
    // block in the sidebar, Shopify dispatches these events on the block
    // element and they bubble up to this component. Jump to that slide and
    // pause autoplay so the merchant can see what they're editing.
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('.announcement-slider__slide');
      if (!slide) return;
      const index = this.slides.indexOf(slide);
      if (index === -1) return;
      this.editing = true;
      this.stopAutoplay();
      this.goTo(index);
    });

    this.addEventListener('shopify:block:deselect', () => {
      this.editing = false;
      this.restartAutoplay();
    });
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay();
    } else {
      this.restartAutoplay();
    }
  }

  handleSwipe() {
    const delta = this.touchEndX - this.touchStartX;
    const threshold = 40;
    if (Math.abs(delta) < threshold) return;

    if (delta < 0) {
      this.goTo(this.currentIndex + 1);
    } else {
      this.goTo(this.currentIndex - 1);
    }
    this.restartAutoplay();
  }

  goTo(index) {
    const total = this.slides.length;
    const nextIndex = (index + total) % total; // always wraps: infinite loop
    if (nextIndex === this.currentIndex) return;

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[nextIndex];

    currentSlide.setAttribute('aria-hidden', 'true');
    currentSlide.tabIndex = -1;

    nextSlide.removeAttribute('aria-hidden');
    nextSlide.tabIndex = -1;

    this.currentIndex = nextIndex;
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.autoplayId || this.slides.length < 2) return;
    if (this.isHovered || this.isFocused || this.editing || document.hidden) return;
    this.autoplayId = window.setInterval(() => this.goTo(this.currentIndex + 1), this.speed);
  }

  stopAutoplay() {
    if (this.autoplayId) {
      window.clearInterval(this.autoplayId);
      this.autoplayId = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('announcement-slider')) {
  customElements.define('announcement-slider', AnnouncementSlider);
}
